"use client";

import type { ImageActionLabels } from "@saas/shared/history/HistoryImageActions";
import {
	ImageHistoryCard,
	type ImageHistoryCardItem,
} from "@saas/shared/history/ImageHistoryCard";
import { VideoHistoryCard } from "@saas/shared/history/VideoHistoryCard";
import type {
	HistoryStatusLabels,
	VideoHistoryItem,
} from "@saas/shared/history/types";
import { memo } from "react";

/**
 * 历史列表的 memo 包装。
 *
 * AiImageGeneratorClient 每次渲染（包括提示词每敲一个字）都会重画整个历史列表，
 * 而卡片 props 里满是 per-item 的内联箭头函数，直接给卡片套 memo 没用。
 * 这里把"稳定的东西"（handlers 集合、label 对象、布尔值）作为 props 传进来，
 * per-item 闭包在包装内部生成，于是敲字时列表一张卡都不会重渲染。
 *
 * handlers 里的函数由父级用 ref 转发，引用永远不变但总是调到最新实现。
 */

export interface ImageRowHandlers {
	openDetail: (item: ImageHistoryCardItem, imageIndex: number) => void;
	reprompt: (item: ImageHistoryCardItem) => void;
	regenerate: (item: ImageHistoryCardItem) => void;
	generateVideo: (imageUrl: string) => void;
	download: (
		imageUrl: string,
		item: ImageHistoryCardItem,
		imageIndex: number,
	) => void;
	edit: (imageUrl: string) => void;
	deleteImage: (itemId: string, imageIndex: number) => void;
	toggleFavorite: (
		itemId: string,
		imageIndex: number,
		isCurrentlyFavorite: boolean,
	) => void;
}

export interface ImageRowLabels {
	typeLabel: string;
	failedMessage: string;
	openDetailLabel: string;
	statusLabels: HistoryStatusLabels;
	actionLabels: ImageActionLabels;
}

interface ImageHistoryRowProps {
	item: ImageHistoryCardItem;
	handlers: ImageRowHandlers;
	labels: ImageRowLabels;
	modelLabel: (model: string) => string;
	isRegenerating: boolean;
	isRegenerateDisabled: boolean;
	/** 这一行每张图的收藏位图（"010"），由父级按当前收藏状态算好。
	 *  只传本行快照：传整个 overrides 对象的话，收藏任意一张图所有行都会重渲染 */
	favoriteKey: string;
}

export const ImageHistoryRow = memo(function ImageHistoryRow({
	item,
	handlers,
	labels,
	modelLabel,
	isRegenerating,
	isRegenerateDisabled,
	favoriteKey,
}: ImageHistoryRowProps) {
	const isFavorite = (imageIndex: number) => favoriteKey[imageIndex] === "1";
	return (
		<ImageHistoryCard
			item={item}
			typeLabel={labels.typeLabel}
			statusLabels={labels.statusLabels}
			modelLabel={modelLabel}
			failedMessage={labels.failedMessage}
			onOpenDetail={(imageIndex) => handlers.openDetail(item, imageIndex)}
			openDetailLabel={labels.openDetailLabel}
			onReprompt={() => handlers.reprompt(item)}
			onRegenerate={() => handlers.regenerate(item)}
			isRegenerating={isRegenerating}
			isRegenerateDisabled={isRegenerateDisabled}
			actionLabels={labels.actionLabels}
			onGenerateVideo={handlers.generateVideo}
			onDownload={(imageUrl, imageIndex) =>
				handlers.download(imageUrl, item, imageIndex)
			}
			onEdit={handlers.edit}
			onDelete={(imageIndex) => handlers.deleteImage(item.id, imageIndex)}
			onToggleFavorite={(imageIndex) =>
				handlers.toggleFavorite(
					item.id,
					imageIndex,
					isFavorite(imageIndex),
				)
			}
			isImageFavorite={isFavorite}
		/>
	);
});

export interface VideoRowHandlers {
	reprompt: (item: VideoHistoryItem) => void;
	regenerate: (item: VideoHistoryItem) => void;
	openDetail: (item: VideoHistoryItem) => void;
	toggleFavorite: (item: VideoHistoryItem) => void;
	deleteVideo: (item: VideoHistoryItem) => void;
}

interface VideoHistoryRowProps {
	items: VideoHistoryItem[];
	handlers: VideoRowHandlers;
	statusLabels: HistoryStatusLabels;
	actionLabels: ImageActionLabels;
	/** 父级按 items 顺序算好的收藏位图（"010"），变了这一行才重渲染 */
	favoriteKey: string;
}

export const VideoHistoryRow = memo(function VideoHistoryRow({
	items,
	handlers,
	statusLabels,
	actionLabels,
	favoriteKey,
}: VideoHistoryRowProps) {
	return (
		<VideoHistoryCard
			items={items}
			onReprompt={handlers.reprompt}
			onRegenerate={handlers.regenerate}
			statusLabels={statusLabels}
			onOpenDetail={handlers.openDetail}
			actionLabels={actionLabels}
			onToggleFavorite={handlers.toggleFavorite}
			isFavorite={(item) => favoriteKey[items.indexOf(item)] === "1"}
			onDelete={handlers.deleteVideo}
		/>
	);
});
